import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AdherenceStatistic } from '../services/adherenceService';

interface AdherenceChartProps {
  data?: AdherenceStatistic[];
  loading?: boolean;
  error?: string | null;
}

// Helper function to format week data for the chart
const formatChartData = (adherenceData: AdherenceStatistic[]) => {
  return adherenceData
    .sort((a, b) => new Date(a.weekStartDate).getTime() - new Date(b.weekStartDate).getTime())
    .map((item) => {
      // Format dates as dd/mm/YY
      const startDate = new Date(item.weekStartDate);
      const endDate = new Date(item.weekEndDate);
      
      const formatDate = (date: Date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        return `${day}/${month}/${year}`;
      };
      
      const weekLabel = formatDate(startDate);
      
      return {
        week: weekLabel,
        adherence: Math.round(item.adherencePercentage),
        fullWeekLabel: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        completedItems: item.completedDiaryItems,
        totalItems: item.totalDiaryItems
      };
    });
};

const AdherenceChart: React.FC<AdherenceChartProps> = ({ 
  data = [], 
  loading = false, 
  error = null 
}) => {
  // Show loading state
  if (loading) {
    return (
      <div style={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>Cargando datos de adherencia...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
          <div>Error al cargar datos</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div style={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>No hay datos de adherencia disponibles</div>
      </div>
    );
  }

  const chartData = formatChartData(data);

  return (
    <div style={{ width: '100%', height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="week" />
          <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
          <Tooltip 
            formatter={(value, name) => {
              const item = chartData.find(d => d.adherence === value);
              return [
                `${value}%`,
                'Adherencia'
              ];
            }}
            labelFormatter={(label) => {
              const item = chartData.find(d => d.week === label);
              return item ? `${item.fullWeekLabel} - ${item.completedItems}/${item.totalItems} completados` : label;
            }}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="adherence" 
            stroke="#38b2ac" 
            strokeWidth={3} 
            dot={{ r: 5, fill: '#38b2ac' }}
            activeDot={{ r: 7, fill: '#38b2ac' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AdherenceChart; 